import React, { useState, useRef, useEffect, useCallback } from 'react';
import { TextField, IconButton, Paper, Typography, Avatar } from '@mui/material';
import { styled } from '@mui/material/styles';

import SendIcon from '@mui/icons-material/Send';
import { grey } from '@mui/material/colors';
import { Message } from './Message.jsx';
import IconBot from '../../assets/images/ICON_bot.svg';
import { useMediBot } from '../../models/MediBot.js';

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.spacing(1),
}));


export function AIChatView({ open = true }) {
  const { getChatMemoized, sendMessageStream } = useMediBot();

  // const { messages: initialMessages, chatId, chatContext, sendMessage: sendMediBotMessage, completeChat, parseAction, loading, error, actionLabel } = useMediBot();

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const chatId = useRef(null);
  const prevOpenRef = useRef(false);

  // Fetch chat data
  const fetchChat = useCallback(() => {
    setIsLoading(true);
    getChatMemoized().then(data => {
      if (data && data.messages) {
        setMessages(data.messages || []);
        chatId.current = data.chat_id;
      }
      setIsLoading(false);
    }).catch(() => {
      setIsLoading(false);
    });
  }, [getChatMemoized]);

  // Load chat when component mounts or when role/context changes
  // Note: fetchChat already depends on getChatMemoized which has the correct dependencies
  useEffect(() => {
    fetchChat();
  }, [fetchChat]);

  // Refetch when modal opens (open changes from false to true)
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      // Modal just opened - refetch
      fetchChat();
    }
    prevOpenRef.current = open;
  }, [open, fetchChat]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || isLoading) return;
    const chat_id = chatId;
    const messageContent = newMessage.trim();

    const userMessage = {
      id: Date.now(),
      content: messageContent,
      author: 'user',
      isUser: true,
      timestamp: new Date(),
      sender: "Usuario"
    };

    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');

    if (chat_id) {
      setIsLoading(true);

      // Create a placeholder for the assistant message
      const assistantMessageId = Date.now() + 1;
      const assistantMessage = {
        id: assistantMessageId,
        content: '',
        author: 'assistant',
        isUser: false,
        timestamp: new Date(),
        sender: "MediBot",
        hidden: true
      };
      setMessages(prev => [...prev, assistantMessage]);

      try {
        await sendMessageStream(
          chatId.current,
          messageContent,
          // onChunk
          (chunk) => {
            // Hide loading indicator once first chunk arrives
            setIsLoading(false);
            setMessages(prev => {
              const newMessages = [...prev];
              const lastMessageIndex = newMessages.length - 1;
              const lastMessage = newMessages[lastMessageIndex];
              if (lastMessage.id === assistantMessageId) {
                // Create a new message object to trigger React re-render
                newMessages[lastMessageIndex] = {
                  ...lastMessage,
                  content: (lastMessage.content || '') + chunk,
                  hidden: false
                };
              }
              return newMessages;
            });
          },
          // onComplete
          (completeData) => {
            setIsLoading(false);

            // If we received structured data, update the message with it
            if (completeData) {
              setMessages(prev => {
                const newMessages = [...prev];
                const lastMessageIndex = newMessages.length - 1;
                const lastMessage = newMessages[lastMessageIndex];
                if (lastMessage.id === assistantMessageId) {
                  newMessages[lastMessageIndex] = {
                    ...lastMessage,
                    ...completeData, // Store full structured response
                    hidden: false
                  };
                }
                return newMessages;
              });
            }

            // Refresh the chat to get the final saved messages with structured data from DB
            fetchChat();
          },
          // onError
          (error) => {
            setIsLoading(false);
            const errorBotMessage = {
              id: assistantMessageId,
              content: `**Error:** ${error || 'Error al enviar el mensaje'}\n\nPor favor, intenta de nuevo o contacta con soporte si el problema persiste.`,
              author: 'assistant',
              isUser: false,
              timestamp: new Date(),
              sender: "Sistema"
            };
            setMessages(prev => {
              const newMessages = [...prev];
              const lastMessageIndex = newMessages.length - 1;
              // Replace the last message with error
              if (newMessages[lastMessageIndex].id === assistantMessageId) {
                newMessages[lastMessageIndex] = errorBotMessage;
              }
              return newMessages;
            });
          }
        );
      } catch (error) {
        setIsLoading(false);
        const errorBotMessage = {
          id: assistantMessageId,
          content: `**Error:** ${error.message || 'Error al enviar el mensaje'}\n\nPor favor, intenta de nuevo o contacta con soporte si el problema persiste.`,
          author: 'assistant',
          isUser: false,
          timestamp: new Date(),
          sender: "Sistema"
        };
        setMessages(prev => {
          const newMessages = [...prev];
          const lastMessageIndex = newMessages.length - 1;
          // Replace the last message with error
          if (newMessages[lastMessageIndex].id === assistantMessageId) {
            newMessages[lastMessageIndex] = errorBotMessage;
          }
          return newMessages;
        });
      }
    }

  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Memoize messages with parsed actions to prevent unnecessary rerenders
  // const messagesWithActions = useMemo(() => {
  //   const action = currentContext ? parseAction(currentContext) : null;
  //   return messages
  //     .filter((message) => message.content)
  //     .map((message, index) => ({
  //       ...message,
  //       // Only add action to the last message if evaluation is ready
  //       parsedAction: (index === messages.length - 1 && currentContext?.evaluation_ready) ? action : null,
  //     }));
  // }, [messages, currentContext, parseAction]);

  // if (isLoading) {
  //   return (<>
  //     cargando...
  //   </>)
  // }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {messages.filter(message => !(message?.hidden ?? false)).map((message, index) => (
          <Message
            key={message.id}
            messageId={message.id}
            chatId={chatId.current}
            content={message.content}
            timestamp={message.timestamp}
            isUser={message.author === 'user' || message.isUser}
            hasAction={message?.hasAction ?? false}
            actionResult={message?.actionResult}
            setNewMessage={setNewMessage}
          />
        ))}

        {isLoading && messages.length > 0 && !messages[messages.length - 1]?.content && (
          <div className="flex items-start gap-3 mb-4">
            <Avatar sx={{ bgcolor: grey[300], width: 32, height: 32 }}>
              <img src={IconBot} alt="AI Bot" style={{ width: '70%', height: '70%' }} />
            </Avatar>
            <div className="bg-gray-100 p-3 rounded-lg rounded-tl-none shadow-sm">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <StyledPaper elevation={2} className="p-4 border-t bg-white">
        <div className="flex gap-2 items-end">
          <TextField
            ref={inputRef}
            fullWidth
            multiline
            maxRows={4}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Escribe tu pregunta aquí..."
            variant="outlined"
            size="small"
            disabled={isLoading}
            className="flex-1"
          />
          <IconButton
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || isLoading}
            color="primary"
            size="large"
            className="mb-1"
          >
            <SendIcon />
          </IconButton>
        </div>
        <Typography variant="caption" className="text-gray-500 mt-1 block">
          Presiona Enter para enviar
        </Typography>
      </StyledPaper>
    </div>
  );
}
